import React, { useState, useCallback } from 'react';
import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import LockIcon from '@atlaskit/icon/glyph/lock';
import Popup from '@atlaskit/popup';
import { Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { useIntl } from '@atlassian/jira-intl';
import { messages } from './messages';

type Props = {
	children: React.ReactNode;
};

const A11Y_HELPER_TEXT_ID =
	'software-backlog.card-list.common.sprint-actions.disabled-button-helper.disabled-button-trigger.a11y-helper-text';

export const DisabledButtonHelper = (props: Props) => {
	const { children } = props;
	const [isDisabledButtonPopupOpen, setIsDisabledButtonPopupOpen] = useState(false);
	const { formatMessage } = useIntl();

	const onDisabledButtonPopupClose = useCallback(() => {
		setIsDisabledButtonPopupOpen(false);
	}, []);

	const handleDisabledButtonClick = useCallback(() => {
		setIsDisabledButtonPopupOpen((prevState) => !prevState);
	}, []);

	const disabledButtonContent = () => (
		<Flex gap="space.100" xcss={widthWrapperStyles}>
			<LockIcon label="" primaryColor={token('color.icon.danger')} />
			<Stack space="space.100">
				<Heading size="xsmall" as="h2">
					{formatMessage(messages.disabledButtonHelperHeadingNonFinal)}
				</Heading>
				<Text as="p">{formatMessage(messages.disabledButtonHelperBodyNonFinal)}</Text>
			</Stack>
		</Flex>
	);

	const disabledButtonTrigger = ({ ...triggerProps }) => (
		<Button
			{...triggerProps}
			isSelected={isDisabledButtonPopupOpen}
			onClick={handleDisabledButtonClick}
			testId="software-board.header.common.sprint-actions.disabled-button-helper.disabled-button-trigger"
			aria-describedby={A11Y_HELPER_TEXT_ID}
			aria-disabled
		>
			{children}
		</Button>
	);

	return (
		<>
			<Popup
				isOpen={isDisabledButtonPopupOpen}
				onClose={onDisabledButtonPopupClose}
				content={disabledButtonContent}
				trigger={disabledButtonTrigger}
				placement="bottom-end"
				testId="software-board.header.common.sprint-actions.disabled-button-helper.disabled-button-popup"
			/>
			<VisuallyHidden
				id={A11Y_HELPER_TEXT_ID}
				testId="software-board.header.common.sprint-actions.disabled-button-helper.a-11-y-sr-text"
			>
				{formatMessage(messages.disabledButtonHelperHeadingNonFinal)}&nbsp;
				{formatMessage(messages.disabledButtonHelperBodyNonFinal)}
			</VisuallyHidden>
		</>
	);
};

const widthWrapperStyles = xcss({
	width: '368px',
	padding: 'space.250',
});
